import React, { useState } from 'react'
import { Avatar, Dropdown, MenuProps } from 'antd'
import Icon from '@ant-design/icons'
import { createPortal } from 'react-dom'
import { createSelector } from '@reduxjs/toolkit'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import './index.less'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import logo_horizontal from '../../../assets/logo_horizontal.svg'
import { arrowDownIcn, changeLanguageIcn, flagEnIcn, flagFrIcn, flagNlIcn, logOutIcn, manageUserIcn } from '../../../assets/CustomIcons'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import { CardinalApiState, logout } from '../../../core/services/auth.api'
import { useGetPractitionerQuery } from '../../../core/api/practitionerApi'
import { getImgSRC } from '../../../helpers/fileToBase64'
import { ModalManageAccount } from '../../user/ModalManageAccount'
import { BurgerMenu } from './BurgerMenu'
import { breakpoints, getWindowSize } from '../../../helpers/windowSize'
import { getAvatarInitials } from '../../../helpers/avatar'
import { getPractitionerName } from '../../../helpers/practitioner'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    user: cardinalApi.user,
    healthcarePartyId: cardinalApi.user?.healthcarePartyId,
  }),
)

export const Header = () => {
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false)
  const [isModalManageAccountFormOpen, setModalManageAccountFormOpen] = useState(false)

  const { innerWidth } = getWindowSize()
  const dispatch = useAppDispatch()
  const { user, healthcarePartyId } = useAppSelector(reduxSelector)
  const { pathname } = useLocation()
  const currentPagePathname = pathname.split('/').filter((p) => p)[0]

  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(healthcarePartyId ?? '', { skip: !healthcarePartyId })

  const userAvatarSrc = getImgSRC(practitioner?.picture)
  const handleLogout = () => {
    dispatch(logout())
  }

  const { t, i18n } = useTranslation()

  const pages = [
    {
      key: 'patients',
      label: t('header.pages.patients'),
      link: '/patients',
    },
    {
      key: 'doctors',
      label: t('header.pages.doctors'),
      link: '/doctors',
    },
  ]
  const userDropdownItems: MenuProps['items'] = [
    {
      key: 'manageAccount',
      label: (
        <div className="header__userDropdown__item">
          <Icon component={manageUserIcn} />
          <span>{t('header.userDropdown.manageAccount')}</span>
        </div>
      ),
    },
    {
      key: 'changeLanguage',
      label: t('header.userDropdown.changeLanguage'),
      icon: <Icon component={changeLanguageIcn} />,
      children: [
        {
          key: 'changeLanguage-en',
          label: t('header.userDropdown.availableLanguages.en'),
          icon: <Icon component={flagEnIcn} />,
        },
        {
          key: 'changeLanguage-fr',
          label: t('header.userDropdown.availableLanguages.fr'),
          icon: <Icon component={flagFrIcn} />,
        },
        {
          key: 'changeLanguage-nl',
          label: t('header.userDropdown.availableLanguages.nl'),
          icon: <Icon component={flagNlIcn} />,
        },
      ],
    },
    {
      key: 'logout',
      danger: true,
      label: (
        <div className="header__userDropdown__item">
          <Icon component={logOutIcn} />
          <span>{t('header.userDropdown.logout')}</span>
        </div>
      ),
    },
  ]
  const onUserDropdownClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'manageAccount': {
        setModalManageAccountFormOpen(true)
        break
      }
      case 'changeLanguage-en': {
        i18n.changeLanguage('en').catch(console.error)
        break
      }
      case 'changeLanguage-fr': {
        i18n.changeLanguage('fr').catch(console.error)
        break
      }
      case 'changeLanguage-nl': {
        i18n.changeLanguage('nl').catch(console.error)
        break
      }
      case 'logout': {
        handleLogout()
        break
      }
    }
  }

  const getUserDropdownOverlay = () => (
    <Dropdown menu={{ items: userDropdownItems, onClick: onUserDropdownClick }} placement="bottomRight" arrow onOpenChange={(open: boolean) => setUserDropdownOpen(open)}>
      <div className={`header__userDropdown ${isUserDropdownOpen && 'header__userDropdown--active'}`}>
        <div className="header__userDropdown__heading">
          <p className="header__userDropdown__heading__name">{getPractitionerName(practitioner)}</p>
          {/*<p className="header__userDropdown__heading__speciality">{practitioner?.speciality ?? 'Speciality'}</p>*/}
        </div>
        {userAvatarSrc ? (
          <div className="header__userDropdown__picture">
            <img src={userAvatarSrc} alt={user?.name ?? t('header.userDropdown.defaultUserAlt')} />
          </div>
        ) : (
          <Avatar style={{ backgroundColor: '#125fa5', color: '#fff', width: '36px', height: '36px' }} size="large">
            {getAvatarInitials(getPractitionerName(practitioner))}
          </Avatar>
        )}
        <div className="header__userDropdown__arrow">
          <Icon component={arrowDownIcn} />
        </div>
      </div>
    </Dropdown>
  )

  const getMobileView = () => {
    return (
      <div className="headerMobileView">
        <BurgerMenu pages={pages} currentPagePathname={currentPagePathname} />
        {getUserDropdownOverlay()}
      </div>
    )
  }

  const getDesktopView = () => (
    <div className="header">
      <div className="header__logoHolder">
        <img src={logo_horizontal} alt={t('logo.alt')} />
      </div>
      <div className="header__rightSection">
        <div className="header__pages">
          {pages.map((page) => (
            <Link key={page.key} to={page.link} className={`header__pages__btn ${currentPagePathname === page.key && 'active'}`}>
              {page.label}
            </Link>
          ))}
        </div>
        {getUserDropdownOverlay()}
      </div>
    </div>
  )

  return (
    <>
      {/*<div className="headerBanner">*/}
      {/*  <p>*/}
      {/*  </p>*/}
      {/*</div>*/}
      {innerWidth > breakpoints.sm ? getDesktopView() : getMobileView()}

      {isModalManageAccountFormOpen &&
        createPortal(
          <ModalManageAccount isVisible={isModalManageAccountFormOpen} onClose={() => setModalManageAccountFormOpen(false)} healthcarePartyId={healthcarePartyId} />,
          document.body,
        )}
    </>
  )
}
